/*
 * Skeleton placeholders shown while a page's data loads. Combined with the
 * React-Query caching, a returning visitor sees cached content instantly and a
 * first-time visitor sees these shimmering shapes instead of a bare
 * "Loading…" string — the app never shows a text loading state again.
 *
 * <Skeleton> is the primitive block; the composed helpers below approximate the
 * real layouts (list rows, tappable cards, dashboard sections) so the swap from
 * skeleton to content doesn't jump.
 */

export function Skeleton({ width, height = 14, radius, style }: {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  style?: React.CSSProperties;
}) {
  return (
    <span
      className="ui-skeleton"
      style={{
        display: 'block',
        width: width ?? '100%',
        height,
        borderRadius: radius,
        ...style,
      }}
    />
  );
}

const cardBase: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 10,
  boxShadow: 'var(--shadow)',
};

/** Rows of a data table (desktop list pages). */
export function SkeletonTable({ rows = 6 }: { rows?: number }) {
  return (
    <div style={{ ...cardBase, overflow: 'hidden' }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '14px 16px',
            borderBottom: i === rows - 1 ? 'none' : '1px solid var(--border-soft)',
          }}
        >
          <Skeleton width={`${40 + ((i * 13) % 30)}%`} />
          <Skeleton width={70} />
        </div>
      ))}
    </div>
  );
}

/** Stacked tappable cards (mobile list pages). */
export function SkeletonCards({ cards = 5 }: { cards?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {Array.from({ length: cards }).map((_, i) => (
        <div key={i} style={{ ...cardBase, padding: 14 }}>
          <Skeleton width="55%" height={15} style={{ marginBottom: 10 }} />
          <Skeleton width="35%" height={11} style={{ marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Skeleton width={64} height={18} radius={10} />
            <Skeleton width={80} height={18} radius={10} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Dashboard-style stacked section cards, each with a heading + a few rows. */
export function SkeletonSections({ sections = 3, rows = 3 }: { sections?: number; rows?: number }) {
  return (
    <div style={{ maxWidth: 1000 }}>
      {Array.from({ length: sections }).map((_, s) => (
        <div key={s} style={{ ...cardBase, padding: 20, marginBottom: 24 }}>
          <Skeleton width={200} height={15} style={{ marginBottom: 18 }} />
          {Array.from({ length: rows }).map((_, r) => (
            <div
              key={r}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: 16,
                padding: '8px 0',
                borderBottom: r === rows - 1 ? 'none' : '1px solid var(--border-soft)',
              }}
            >
              <Skeleton width={`${45 + ((r * 17) % 25)}%`} height={13} />
              <Skeleton width={90} height={13} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
